import { useEffect, useRef, useState } from 'react';

import { useTRPC } from './useTRPC';

const URL_VALIDATION_DEBOUNCE_MS = 1_200;

export type ExternalUrlValidationStatus =
  | 'checking'
  | 'idle'
  | 'invalid'
  | 'pending'
  | 'valid'
  | 'warning';

type ExternalUrlValidationState = {
  blocking: boolean;
  message: string;
  status: ExternalUrlValidationStatus;
};

export function getExternalUrlValidationMessageClassName(
  status: ExternalUrlValidationStatus,
  hasLocalError = false,
) {
  if (hasLocalError || status === 'invalid') {
    return 'text-rose-500';
  }

  if (status === 'valid') {
    return 'text-emerald-400';
  }

  return status === 'warning' ? 'text-amber-400' : 'text-neutral-400';
}

function isAdvisoryFailureReason(reason: unknown) {
  return (
    reason === 'rate_limited' ||
    reason === 'request_failed' ||
    reason === 'request_timeout'
  );
}

function getAdvisoryMessage(reason: unknown) {
  return reason === 'rate_limited'
    ? "We're checking too many links right now. We'll retry on save."
    : "We couldn't check this link. We'll retry on save.";
}

export function useExternalUrlValidation(
  url: string,
  options: {
    debounceMs?: number;
    enabled?: boolean;
  } = {},
): ExternalUrlValidationState {
  const { debounceMs = URL_VALIDATION_DEBOUNCE_MS, enabled = true } = options;
  const trpc = useTRPC();
  const requestIdRef = useRef(0);
  const [state, setState] = useState<ExternalUrlValidationState>({
    blocking: false,
    message: '',
    status: 'idle',
  });

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const normalizedUrl = url.trim();

    if (!enabled || !normalizedUrl) {
      setState({ blocking: false, message: '', status: 'idle' });
      return;
    }

    setState({
      blocking: false,
      message: 'Checking link...',
      status: 'pending',
    });

    const timeout = window.setTimeout(() => {
      setState({
        blocking: false,
        message: 'Checking link...',
        status: 'checking',
      });

      void trpc.validateExternalUrl
        .query({ url: normalizedUrl })
        .then((result) => {
          if (requestId !== requestIdRef.current) {
            return;
          }

          if (result._tag === 'Left') {
            setState({
              blocking: false,
              message: "We couldn't check this link. We'll retry on save.",
              status: 'warning',
            });
            return;
          }

          const advisoryFailure = isAdvisoryFailureReason(result.right.reason);
          setState({
            blocking: !result.right.valid && !advisoryFailure,
            message: advisoryFailure
              ? getAdvisoryMessage(result.right.reason)
              : result.right.message,
            status: result.right.valid
              ? 'valid'
              : advisoryFailure
                ? 'warning'
                : 'invalid',
          });
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) {
            return;
          }

          setState({
            blocking: false,
            message: "We couldn't check this link. We'll retry on save.",
            status: 'warning',
          });
        });
    }, debounceMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [debounceMs, enabled, trpc, url]);

  return state;
}
